import { useEffect, useState } from "react";
import { useAppSelector } from "../../hooks/redux";
import store, { RootState } from "../../store/store";
import Button from "../common/Button";
import { gameActions } from "../Game/gameSlice";
import "./Matchmaking.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { LocalStorageKeys } from "../../models/LocalStorageKeys";
import Logo from "../Logo/Logo";
import PlayerPicture from "../Players/PlayerPicture";
import { FALLBACK_AVATAR } from "../../utils/enums";
import { shortText } from "../../utils/formatting";
import { GameStatus } from "../../models/GameStatus";
import About from "../About/About";

export default function Matchmaking() {

  const navigate = useNavigate();
  const location = useLocation();
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  const gameState = useAppSelector((state: RootState) => state.game);
  const userState = useAppSelector((state: RootState) => state.user);
  const gameListState = useAppSelector((state: RootState) => state.gameList);

  const localToken = localStorage.getItem(LocalStorageKeys.TOKEN_KEY);

  useEffect(() => {

    if (!localToken) {
      if (location.pathname !== '/') {
        navigate(`/`, { replace: true });
      }
      return;
    }

    store.dispatch(gameActions.sendToken({
      token: localToken,
      gameId: undefined
    }));

  }, [gameState.isConnected, localToken, navigate, location.pathname]);

  function newGame(disableNotifications: boolean = false) {
    store.dispatch(gameActions.sendCreateNewGame({
      token: gameState.token,
      disableNotifications: disableNotifications,
    }));
  }

  function stopGame(gameId: string) {
    if (gameId) {
      store.dispatch(gameActions.sendEndSession({
        gameId: gameId,
        adminId: userState.user.id,
      }));
    }
  }

  function kick(gameId: string, playerId: string) {
    if (gameId && gameState.isConnected) {
      if (window.confirm('are you sure you want to kick this player?')) {
        store.dispatch(gameActions.sendKick({
          gameId: gameId,
          playerId: playerId,
          adminId: userState.user.id,
        }));
      }
    }
  }

  function saveGame(gameId: string) {
    if (gameId) {
      store.dispatch(gameActions.sendSave({
        gameId: gameId,
        adminId: userState.user.id,
      }));
    }
  }

  function joinGame(gameId: string) {
    if (gameState.token) {
      navigate(`/${gameId}`, { replace: true });
    }
  }

  function clickSettings() {
    if (gameState.token) {
      navigate(`/settings`, { replace: true });
    }
  }

  function clickStats() {
    if (gameState.token) {
      navigate(`/stats`, { replace: true });
    }
  }

  function clickAbout() {
    if (gameState.token) {
      navigate(`/about`, { replace: true });
    }
  }

  const validGames = gameListState.gameList.filter(it => !!it && it.gameId);

  const gameList = validGames && validGames.length
    ? validGames
    : [{
      gameId: '',
      status: '',
      turnNumber: null,
      gameStarted: false,
      playerList: [],
      gameAdmins: [],
    }];

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  }

  const playerData = {
    username: userState.user.name,
    picture: userState.user.dixitAvatar ? userState.user.picture : FALLBACK_AVATAR,
  };

  const isGameAdmin = gameListState.admins.includes(userState.user.id);

  if (!localToken) {
    return <About />;
  }

  return (
    <div className="matchmaking">
      {!gameState.token && <><Logo /></>}
      {gameState.token && <>
        <div className="matchmaking-heading">
          <div className="flex-row">
            {playerData && <PlayerPicture player={playerData} size={26} hideInitials={true} />}
            <div className="matchmaking-heading_name no-user-select">{userState.user.name}</div>
          </div>
          <div className="flex-row">
            <div className="matchmaking-heading_menu pointer" onClick={clickAbout}>
              <img src={'about.png'} alt="settings" />
            </div>
            <div className="matchmaking-heading_menu pointer" onClick={clickStats}>
              <img src={'stats.png'} alt="settings" />
            </div>
            <div className="matchmaking-heading_menu pointer" onClick={clickSettings}>
              <img src={'gear.png'} alt="settings" />
            </div>
          </div>
          {/* <div className="stats-icon pointer no-select" onClick={clickStats}>☷</div> */}
          {/* <div className="settings-icon pointer no-select" onClick={clickSettings}>&#9965;</div> */}
        </div>
        <div className="matchmaking-content">

          <div className="matchmaking_admin">
            {/* token, admin mode toggle */}
          </div>

          <div className="matchmaking_actions">
            <Button
              classes={`matchmaking_actions_item`}
              onClick={() => newGame(!notificationsEnabled)}
              text="New game"
              disabled={!gameState.isConnected}
              confirmPrompt={`Are you sure you want to start a new game ${notificationsEnabled ? 'with' : 'without'} notifications?`}
            />

            {isGameAdmin && <>
              <div className="matchmaking_actions_item matchmaking_actions_item_checkbox">
                <label>
                  <input type="checkbox"
                    checked={notificationsEnabled}
                    onChange={(e) => setNotificationsEnabled(e.target.checked)}
                  />
                  <span>telegram notifications</span>
                </label>
              </div>
            </>}
          </div>


          <div className="matchmaking_games">

            <table><thead>
              <tr>
                <th>#</th>
                <th>game id</th>
                <th>action</th>
                <th>status</th>
                <th>turn</th>
                <th>players</th>
                <th onDoubleClick={() => { } /* toggleAdminMode() */}>admin</th>
              </tr></thead>
              <tbody>
                {gameList.reverse().map((game, id) => {

                  const isPlayerJoined = game.playerList.find(it => it.playerId === userState.user.id);

                  const enterText = isPlayerJoined
                    ? 'Enter >'
                    : game.gameStarted
                      ? 'Spectate >'
                      : 'Join >'


                  const isAdmin = game.gameAdmins
                    ? (game.gameAdmins as string[]).includes(userState.user.id)
                    : false;

                  return (
                    <tr key={id}>
                      <td>{game.gameId ? gameList.length - id : '-'}</td>
                      <td className="matchmaking-table_id">{shortText(game.gameId) || '-'}</td>
                      <td className="matchmaking-table_actions">
                        {!game.gameId && <>-</>}
                        {game.gameId &&
                          <Button onClick={() => joinGame(game.gameId)} text={enterText} disabled={false} classes={isPlayerJoined ? 'green' : ''} />
                        }
                      </td>
                      <td className="matchmaking-table_status">{game.status || '-'}</td>
                      <td className="matchmaking-table_round">{typeof game.turnNumber === 'number' ? `turn: ${game.turnNumber}` : '-'}</td>
                      <td className="matchmaking-table_players">
                        {game.playerList && game.playerList.map((player, i) => {
                          const playerClass = player.isHost ? 'highlight-text' : '';
                          return (
                            <div key={i}>
                              {/* {player.isHost && <span className="player-host">♛</span>} */}
                              <span className={playerClass}>{player.username}</span>
                              {isAdmin && <span
                                title="kick player"
                                className="pointer player-remove danger-text"
                                onClick={() => kick(game.gameId, player.playerId)}>⌫</span>
                              }
                            </div>
                          )
                        })}
                        {(!game.playerList || !game.playerList.length) && <>-</>}
                      </td>
                      <td className="matchmaking-table_actions">
                        {!game.gameId && <>-</>}
                        {game.gameId && isAdmin && <>
                          {game.status !== GameStatus.GAME_END &&
                            <Button
                              onClick={() => saveGame(game.gameId)}
                              text="💾"
                              confirmPrompt={'are you sure you want to end the game and save the results?'}
                              classes="danger"
                              disabled={!isAdmin}
                            ></Button>
                          }
                          <Button
                            onClick={() => stopGame(game.gameId)}
                            text="🗑️"
                            confirmPrompt={'are you sure you want to stop and remove this game?'}
                            classes="danger"
                            disabled={!isAdmin}
                          ></Button>
                          <br />
                        </>
                        }
                      </td>
                    </tr>
                  )
                })
                }
              </tbody>
            </table>
          </div>
        </div>

      </>}
    </div >
  )
}
